/**
 * FinanceLocal — analytics helper
 *
 * Thin wrappers around window.gtag (GA4) + window.clarity (Microsoft Clarity).
 * Both scripts are loaded synchronously in public/index.html so they exist
 * globally by the time React mounts. Every helper is a no-op if the tag is
 * blocked by an ad-blocker — never throws, never breaks the UI.
 *
 * Standard GA4 event names used verbatim (login, sign_up, page_view) so
 * they populate GA4's built-in conversion columns automatically. Custom
 * events (note_viewed, preapproval_*, offer_submitted, etc.) show up in
 * Explore > Events without extra config.
 */

const _gtag = (...args) => {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag(...args);
    }
  } catch { /* silent */ }
};

const _clarity = (...args) => {
  try {
    if (typeof window !== "undefined" && typeof window.clarity === "function") {
      window.clarity(...args);
    }
  } catch { /* silent */ }
};

/** Fire a GA4 event with optional params. Never throws. */
export const track = (event, params = {}) => {
  _gtag("event", event, params);
  // Mirror the event name into Clarity as a custom tag so replays are filterable.
  _clarity("event", event);
};

/** Set the current user id + role on both tools (post-login). */
export const identify = (userId, traits = {}) => {
  if (!userId) return;
  _gtag("set", { user_id: String(userId) });
  if (traits.role) {
    _gtag("set", "user_properties", { role: traits.role });
    _clarity("set", "role", traits.role);
  }
  _clarity("identify", String(userId));
};

/** Manual page view (used on SPA route change since GA4 auto-fires only on load). */
export const pageView = (path, title) => {
  _gtag("event", "page_view", {
    page_path: path,
    page_location: typeof window !== "undefined" ? window.location.href : path,
    page_title: title || (typeof document !== "undefined" ? document.title : ""),
  });
};

// ---------------------------------------------------------------------------
// Named conversion events (spec-defined)
// ---------------------------------------------------------------------------
export const trackNoteViewed = (note) => track("note_viewed", {
  note_id: note?.note_id,
  grade: note?.grade,
  category: note?.category,
  asking_price: note?.asking_price,
  estimated_yield: note?.estimated_yield,
  state: note?.state,
  vehicle: note?.vehicle_make && note?.vehicle_model
    ? `${note.vehicle_year} ${note.vehicle_make} ${note.vehicle_model}` : undefined,
});

export const trackCreateAccount = (method, role) => {
  // GA4 built-in: sign_up (shows in default reports)
  track("sign_up", { method, role });
  // Alias with the spec name for dashboards
  track("create_account", { method, role });
};

export const trackLogin = (method, role) => {
  track("login", { method, role });
};

export const trackPreapprovalStarted = (payload = {}) => track("preapproval_started", payload);
export const trackPreapprovalCompleted = (payload = {}) => track("preapproval_completed", payload);

export const trackOfferSubmitted = (note, offerAmount) => track("offer_submitted", {
  note_id: note?.note_id,
  offer_amount: offerAmount,
  asking_price: note?.asking_price,
  currency: "USD",
  value: offerAmount,
});

export const trackDealerSignup = (method) => track("dealer_signup", { method });

export const trackOutboundLinkClick = (href, label) => track("outbound_link_click", {
  outbound_url: href,
  link_label: label,
});

export const trackContactSubmission = (subject) => track("contact_submission", { subject });
